import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Select,
    MenuItem,
    TextField,
    FormControl,
    Modal,
    Paper
} from '@mui/material';
import { GoogleMap, useJsApiLoader, Polygon } from '@react-google-maps/api';
import authService from '../../api-authorization/AuthorizeService';
import { getCoordinatesFromPolygonText } from '../../../utils';
import { useNavigate } from 'react-router-dom';

const defaultCenter = { lat: 36.55, lng: -119.75 };

function BlocksImport() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [rows, setRows] = useState([]);
    const [farms, setFarms] = useState([]);
    const [crops, setCrops] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectAllMap, setSelectAllMap] = useState(false); // New: Select all for Map column
    const [recommendedLocationNameAttribute, setRecommendedLocationNameAttribute] = useState('');
    const [recommendedSubLocationNameAttribute, setRecommendedSubLocationNameAttribute] = useState('');
    const [recommendedCropAttribute, setRecommendedCropAttribute] = useState('');
    const [recommendedRowWidthFtAttribute, setRecommendedRowWidthFtAttribute] = useState('');
    const [attributes, setAttributes] = useState([]);
    const [shapefile, setShapefile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Add these state variables near other useState declarations
    const [newFarm, setNewFarm] = useState('');
    const [newCrop, setNewCrop] = useState('');
    const [newRowWidth, setNewRowWidth] = useState('');
    const [attributesModalOpen, setAttributesModalOpen] = useState(false);
    const [selectedAttributes, setSelectedAttributes] = useState({});

    // Map state
    const mapRef = useRef(null);
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY'
    });

    // Selected rows for map display (now based on isMapSelected)
    const mapRows = useMemo(
        () => rows.filter(row => row.isMapSelected),
        [rows]
    );

    // --- Calculate mapCenter as the center of all selected blocks ---
    const mapCenter = useMemo(() => {
        if (mapRows.length > 0) {
            let bounds = { minLat: null, maxLat: null, minLng: null, maxLng: null };
            mapRows.forEach(row => {
                if (row.boundaries) {
                    const polygons = getCoordinatesFromPolygonText(row.boundaries);
                    polygons.forEach(coords => {
                        coords.forEach(coord => {
                            if (bounds.minLat === null || coord.lat < bounds.minLat) bounds.minLat = coord.lat;
                            if (bounds.maxLat === null || coord.lat > bounds.maxLat) bounds.maxLat = coord.lat;
                            if (bounds.minLng === null || coord.lng < bounds.minLng) bounds.minLng = coord.lng;
                            if (bounds.maxLng === null || coord.lng > bounds.maxLng) bounds.maxLng = coord.lng;
                        });
                    });
                }
            });
            if (bounds.minLat !== null && bounds.maxLat !== null && bounds.minLng !== null && bounds.maxLng !== null) {
                return {
                    lat: (bounds.minLat + bounds.maxLat) / 2,
                    lng: (bounds.minLng + bounds.maxLng) / 2
                };
            }
        }
        return defaultCenter;
    }, [rows.map(r => r.isMapSelected).join(',')]);

    // --- Fit map to all selected polygons whenever selection changes or map loads ---
    useEffect(() => {
        if (!isLoaded || !mapRef.current || mapRows.length === 0 || !window.google || !window.google.maps) return;

        const bounds = new window.google.maps.LatLngBounds();
        let hasValid = false;
   
        mapRows.forEach(row => {
        const polygons = getCoordinatesFromPolygonText(row.boundaries);
        polygons.forEach(polygon => {
            polygon.forEach(coord => {
                bounds.extend(coord);
                hasValid = true;
            });
        });
    });

    if (hasValid) {
        mapRef.current.fitBounds(bounds);
    }
}, [rows.map(r => r.isMapSelected).join(','), isLoaded]);

    // --- Ensure all blocks are selected for map display on initial load ---
    const initialMapSelectionDone = useRef(false);

    useEffect(() => {
        if (
            rows.length > 0 &&
            rows.every(r => r.isMapSelected !== true) &&
            !initialMapSelectionDone.current
        ) {
            setRows(rows.map(r => ({ ...r, isMapSelected: true })));
            initialMapSelectionDone.current = true;
        }
        // Only run when rows change (initial load or after upload/refresh)
        // eslint-disable-next-line
    }, [rows]);

    // Fetch farms and crops for dropdowns (example endpoints, adjust as needed)
    React.useEffect(() => {
        async function fetchDropdowns() {
            try {
                const token = await authService.getAccessToken();
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const farmsRes = await fetch(`${process.env.REACT_APP_BASE_URL}/api/locations/all`, { headers, credentials: 'include' });
                const farmsData = await farmsRes.json();
                setFarms(Array.isArray(farmsData) ? farmsData : []);
                const cropsRes = await fetch(`${process.env.REACT_APP_BASE_URL}/api/crops/all`, { headers, credentials: 'include' });
                const cropsData = await cropsRes.json();
                setCrops(Array.isArray(cropsData) ? cropsData : []);
            } catch {
                setFarms([]);
                setCrops([]);
            }
        }
        fetchDropdowns();
    }, []);

    // Handle file selection
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
            setResult(null);
        }
    };

    // Store existing block names from last import/refresh response
    const [existingSubLocationNames, setExistingSubLocationNames] = useState([]);

    // --- Helper to get attribute value from attributeValues for a given row ---
    // Now attributeValues is a dictionary: { [attributeName]: value }
    function getAttributeValue(row, attributeName) {
        if (!row.attributeValues || !attributeName) return '';
        return row.attributeValues[attributeName] ?? '';
    }

    // Update validateRows to require farm name (locationName)
    // Update validateRows so isSelected is not forcibly set to false if errors are present
    function validateRows(rows, existingNames = []) {
        const existingSet = new Set(existingNames.map(n => n.trim().toLowerCase()));
        const nameCounts = {};
        rows.forEach(row => {
            const name = (row.name ?? '').trim().toLowerCase();
            if (name) {
                nameCounts[name] = (nameCounts[name] || 0) + 1;
            }
        });

        return rows.map((row) => {
            const name = (row.name ?? '').trim().toLowerCase();
            const farmName = (row.farmName ?? '').trim();
            const messages = [];

            if (!farmName) {
                messages.push('Farm name is required.');
            }
            // --- Block name cannot be blank ---
            if (!name) {
                messages.push('Block name is required.');
            }
            const duplicateMsg = 'Block name is duplicated in this import.';
            if (name && nameCounts[name] > 1 && !messages.includes(duplicateMsg)) {
                messages.push(duplicateMsg);
            }
            const existsMsg = 'There is an existing block with this name.';
            if (name && existingSet.has(name) && !messages.includes(existsMsg)) {
                messages.push(existsMsg);
            }

            // Only allow import if the original canImport from API is true AND there are no validation errors
            const canImport = row.originalCanImport === true && messages.length === 0;
            return {
                ...row,
                canImport,
                message: canImport ? 'Ready to import' : messages.join('\n')
            };
        });
    }

    // Select All logic for Import column
    const handleSelectAllChange = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        setRows(rows.map(row =>
            row.canImport
                ? { ...row, isSelected: checked }
                : { ...row, isSelected: false }
        ));
    };

    // Select All logic for Map column
    const handleSelectAllMapChange = (e) => {
        const checked = e.target.checked;
        setSelectAllMap(checked);
        setRows(rows.map(row =>
            ({ ...row, isMapSelected: checked })
        ));
    };

    // Add error dialog state
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);

    // Modified handleUpload to validate names only on import click
    const handleUpload = async () => {
        resetForm();
        if (!selectedFile) return;
        setUploading(true);
        setResult(null);

        const formData = new FormData();
        formData.append('File', selectedFile);

        try {
            const token = await authService.getAccessToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/sublocations/process-shapefile`,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                    headers,
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                setResult({ error: errorText });
                setRows([]);
                setShapefile(null);
            } else {
                const data = await response.json();
                setResult({ success: data });

                setShapefile(data.shapeFile ?? null);
                setRecommendedLocationNameAttribute(data.recommendedLocationNameAttribute || '');
                setRecommendedSubLocationNameAttribute(data.recommendedSubLocationNameAttribute || '');
                setRecommendedCropAttribute(data.recommendedCropAttribute || '');
                setRecommendedRowWidthFtAttribute(data.recommendedRowWidthFtAttribute || '');
                setAttributes(Array.isArray(data.attributes) ? data.attributes : []);

                setExistingFarmNames(Array.isArray(data.existingFarmNames) ? data.existingFarmNames : []);
                setExistingCrops(Array.isArray(data.existingCrops) ? data.existingCrops : []);
                setExistingRowWidthFts(Array.isArray(data.existingRowWidthFts) ? data.existingRowWidthFts : []);

                const rows =
                    Array.isArray(data.subLocations)
                        ? data.subLocations.map(row => ({
                            ...row,
                            farmName: row.attributeValues?.[data.recommendedLocationNameAttribute] ?? row.farmName ?? '',
                            crop: row.attributeValues?.[data.recommendedCropAttribute] ?? row.crop ?? '',
                            rowWidthFt: row.attributeValues?.[data.recommendedRowWidthFtAttribute] || row.rowWidthFt || 0,
                            name: row.attributeValues?.[data.recommendedSubLocationNameAttribute] ?? row.blockName ?? '',
                            attributeValues: row.attributeValues ?? {},
                            isSelected: !!row.canImport,
                            isMapSelected: true,
                            canImport: !!row.canImport,
                            originalCanImport: !!row.canImport,
                            message: row.message ?? ''
                        }))
                        : [];
                setRows(rows);
                setExistingSubLocationNames(Array.isArray(data.existingSubLocationNames) ? data.existingSubLocationNames : []);
            }
        } catch (err) {
            setResult({ error: err.message });
            setRows([]);
            setShapefile(null);
        } finally {
            setUploading(false);
        }
    };

    // Modified handleSave to validate names once, show error dialog if needed
    const handleSave = async () => {
        // Only validate rows that are selected for import
        const selectedRows = rows.filter(row => row.isSelected);
        const validatedRows = validateRows(selectedRows, existingSubLocationNames);

        // Merge validation results back into the full rows array
        const updatedRows = rows.map(row => {
            const validated = validatedRows.find(vr => vr.name === row.name);
            return validated ? validated : row;
        });
        setRows(updatedRows);

        // Check for any errors in selected rows
        const hasErrors = validatedRows.some(row => row.message && row.message !== 'Ready to import');
        if (hasErrors) {
            setErrorDialogOpen(true);
            return;
        }

        setSaving(true);
        setSaveResult(null);

        const subLocationsToSave = validatedRows.filter(row => row.isSelected && row.canImport);
        const requestBody = { subLocations: subLocationsToSave };

        try {
            const token = await authService.getAccessToken();
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            };

            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/sublocations/save-shapefile`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(requestBody),
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                setSaveResult({ error: errorText });
            } else {
                const data = await response.json();
                setSaveResult({ success: data });
                setModalOpen(true);
            }
        } catch (err) {
            setSaveResult({ error: err.message });
        } finally {
            setSaving(false);
        }
    };

    // Add state for selected existing mappings
    const [selectedExistingFarm, setSelectedExistingFarm] = useState('');
    const [selectedExistingCrop, setSelectedExistingCrop] = useState('');
    const [selectedExistingRowWidth, setSelectedExistingRowWidth] = useState('');

    // Add state for options returned from API
    const [existingFarmNames, setExistingFarmNames] = useState([]);
    const [existingCrops, setExistingCrops] = useState([]);
    const [existingRowWidthFts, setExistingRowWidthFts] = useState([]);

    const navigate = useNavigate();

    // Helper function to reset the form
    const resetForm = () => {
        setSelectedFile(null);
        setResult(null);
        setRows([]);
        setFarms([]);
        setCrops([]);
        setSelectAll(false);
        setSelectAllMap(false);
        setRecommendedLocationNameAttribute('');
        setRecommendedSubLocationNameAttribute('');
        setRecommendedCropAttribute('');
        setRecommendedRowWidthFtAttribute('');
        setAttributes([]);
        setShapefile(null);
        setNewFarm('');
        setNewCrop('');
        setNewRowWidth('');
        setExistingSubLocationNames([]);
        setSelectedExistingFarm('');
        setSelectedExistingCrop('');
        setSelectedExistingRowWidth('');
        setExistingFarmNames([]);
        setExistingCrops([]);
        setExistingRowWidthFts([]);
        setSaveResult(null);
        setModalOpen(false);
        setErrorDialogOpen(false);
        setAttributesModalOpen(false);
        setSelectedAttributes({});
    };

    // --- React component render ---
    return (
        <div
            style={{
                alignItems: 'stretch',
                background: 'white',
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                padding: '40px 40px'
            }}
        >
            <Box sx={{ p: 3 }}>
                <label htmlFor="blocks-import-zip">
                    <strong>Upload shape file zip</strong>
                </label>
                <input
                    id="blocks-import-zip"
                    type="file"
                    accept=".zip"
                    style={{ display: 'block', margin: '12px 0' }}
                    onChange={handleFileChange}
                    disabled={uploading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    sx={{ fontWeight: 'bold', px: 3, py: 1, borderRadius: 2 }}
                >
                    Process
                </Button>
                {uploading && <Typography sx={{ mt: 2 }}>Uploading...</Typography>}
                {result && result.error && (
                    <Typography sx={{ color: 'red', mt: 2 }}>
                        Error: {result.error}
                    </Typography>
                )}
                {result && result.success && (
                    <Typography sx={{ color: 'green', mt: 2 }}>
                        Successfully processed shape file
                    </Typography>
                )}

                {/* Top comboboxes for Farm, Block, Crop, Row Width Attribute */}
                {rows.length > 0 && (
                    <>
                        {/* Attribute Mapping */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Attribute Mapping
                        </Typography>
                        <Box
                            sx={{
                                mb: 2,
                                p: 3, // Match padding
                                border: '2px solid #000', // Match border
                                borderRadius: 2,
                                background: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Select which shapefile attributes should be mapped to each field below.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Farm Attribute</Typography>
                                    <Select
                                        displayEmpty
                                        value={recommendedLocationNameAttribute}
                                        onChange={e => {
                                            setRecommendedLocationNameAttribute(e.target.value);
                                            setRows(rows.map(row => ({
                                                ...row,
                                                farmName: row.attributeValues?.[e.target.value] ?? row.farmName ?? ''
                                            })));
                                        }}
                                    >
                                        {attributes.map(attr => (
                                            <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Block Attribute</Typography>
                                    <Select
                                        displayEmpty
                                        value={recommendedSubLocationNameAttribute}
                                        onChange={e => {
                                            setRecommendedSubLocationNameAttribute(e.target.value);
                                            setRows(rows.map(row => ({
                                                ...row,
                                                name: row.attributeValues?.[e.target.value] ?? row.name ?? ''
                                            })));
                                        }}
                                    >
                                        {attributes.map(attr => (
                                            <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Crop Attribute</Typography>
                                    <Select
                                        displayEmpty
                                        value={recommendedCropAttribute}
                                        onChange={e => {
                                            setRecommendedCropAttribute(e.target.value);
                                            setRows(rows.map(row => ({
                                                ...row,
                                                crop: row.attributeValues?.[e.target.value] ?? row.crop ?? ''
                                            })));
                                        }}
                                    >
                                        {attributes.map(attr => (
                                            <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Row Width Attribute</Typography>
                                    <Select
                                        displayEmpty
                                        value={recommendedRowWidthFtAttribute}
                                        onChange={e => {
                                            setRecommendedRowWidthFtAttribute(e.target.value);
                                            setRows(rows.map(row => ({
                                                ...row,
                                                rowWidthFt: row.attributeValues?.[e.target.value] ?? row.rowWidthFt ?? ''
                                            })));
                                        }}
                                    >
                                        {attributes.map(attr => (
                                            <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Existing Data Mapping label moved outside the container */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Existing Data Mapping
                        </Typography>
                        <Box
                            sx={{
                                mb: 2,
                                p: 3, // Match padding
                                border: '2px solid #000', // Match border
                                borderRadius: 2,
                                background: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            {/* Description label for Existing Data Mapping */}
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Apply a single value from your existing data to all imported blocks for each field below.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Existing Farms</Typography>
                                    <Select
                                        displayEmpty
                                        value={selectedExistingFarm}
                                        onChange={e => {
                                            const selectedValue = e.target.value;
                                            setSelectedExistingFarm(selectedValue);
                                            setRows(rows =>
                                                rows.map(row => ({
                                                    ...row,
                                                    farmName: selectedValue
                                                }))
                                            );
                                        }}
                                    >
                                        {existingFarmNames.map(farm => (
                                            <MenuItem key={farm} value={farm}>{farm}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Existing Crops</Typography>
                                    <Select
                                        displayEmpty
                                        value={selectedExistingCrop}
                                        onChange={e => {
                                            const selectedValue = e.target.value;
                                            setSelectedExistingCrop(selectedValue);
                                            setRows(rows =>
                                                rows.map(row => ({
                                                    ...row,
                                                    crop: selectedValue
                                                }))
                                            );
                                        }}
                                    >
                                        {existingCrops.map(crop => (
                                            <MenuItem key={crop} value={crop}>{crop}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>Existing Row Widths (ft)</Typography>
                                    <Select
                                        displayEmpty
                                        value={selectedExistingRowWidth}
                                        onChange={e => {
                                            const selectedValue = e.target.value;
                                            setSelectedExistingRowWidth(selectedValue);
                                            setRows(rows =>
                                                rows.map(row => ({
                                                    ...row,
                                                    rowWidthFt: selectedValue
                                                }))
                                            );
                                        }}
                                    >
                                        {existingRowWidthFts.map(width => (
                                            <MenuItem key={width} value={width}>{width}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            New Data Mapping
                        </Typography>
                        <Box
                            sx={{
                                mb: 2,
                                p: 3,
                                border: '2px solid #000',
                                borderRadius: 2,
                                background: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            {/* Description label for New Data Mapping */}
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Enter new values to apply to all imported blocks for each field below.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>New Farm</Typography>
                                    <TextField
                                        value={newFarm}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setNewFarm(value);
                                            setRows(rows => rows.map(row => ({ ...row, farmName: value })));
                                        }}
                                        size="small"
                                        variant="outlined"
                                    />
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>New Crop</Typography>
                                    <TextField
                                        value={newCrop}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setNewCrop(value);
                                            setRows(rows => rows.map(row => ({ ...row, crop: value })));
                                        }}
                                        size="small"
                                        variant="outlined"
                                    />
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>New Row Width (ft)</Typography>
                                  <TextField
                                    value={newRowWidth}
                                    onChange={e => {
                                      const value = e.target.value;
                                      setNewRowWidth(value);
                                      setRows(rows => rows.map(row => ({ ...row, rowWidthFt: value })));
                                    }}
                                    size="small"
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                  />
                                </FormControl>
                            </Box>
                        </Box>
                    </>
                )}

                {/* Headings on same line */}
                {rows.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 8,
                        gap: '24px'
                    }}>
                        <div style={{ flex: 7 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Blocks To Import
                            </Typography>
                        </div>
                        <div style={{ flex: 5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                Selected Block Boundaries
                            </Typography>
                        </div>
                    </div>
                )}

                {/* Table and Map aligned at the top */}
                {rows.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '24px'
                    }}>
                        <div style={{
                            flex: 7,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '900px'
                        }}>
                            <TableContainer
                                sx={{
                                    mt: 0,
                                    border: '2px solid #000',
                                    borderRadius: 2,
                                    overflow: 'auto',
                                    background: '#fff',
                                    flex: 1,
                                    height: '900px',
                                    maxHeight: '900px'
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={selectAll}
                                                    onChange={handleSelectAllChange}
                                                />
                                                Import
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={rows.length > 0 && rows.every(r => r.isMapSelected)}
                                                    indeterminate={rows.some(r => r.isMapSelected) && !rows.every(r => r.isMapSelected)}
                                                    onChange={handleSelectAllMapChange}
                                                />
                                                Map
                                            </TableCell>
                                            <TableCell align="center">Farm</TableCell>
                                            <TableCell align="center">Block</TableCell>
                                            <TableCell align="center">Size (ac)</TableCell>
                                            <TableCell align="center">Crop</TableCell>
                                            <TableCell align="center">Row Width (ft)</TableCell>
                                            <TableCell align="center">Message</TableCell>
                                            <TableCell align="center">Attributes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    backgroundColor: row.message && row.message !== 'Ready to import'
                                                        ? '#ffe6e6' // error: light red
                                                        : row.canImport
                                                            ? '#e6ffe6' // valid: light green
                                                            : '#fff' // default
                                                }}
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={row.isSelected}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, isSelected: e.target.checked } : r
                                                            )
                                                        )}
                                                        disabled={!row.originalCanImport}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={row.isMapSelected}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, isMapSelected: e.target.checked } : r
                                                            )
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={row.farmName ?? ''}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, farmName: e.target.value } : r
                                                            )
                                                        )}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={row.name ?? ''}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, name: e.target.value } : r
                                                            )
                                                        )}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {row.areaAcres !== undefined && row.areaAcres !== null && row.areaAcres !== ''
                                                        ? Number(row.areaAcres).toFixed(1)
                                                        : ''}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={row.crop ?? row.crop ?? ''}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, crop: e.target.value, crop: e.target.value } : r
                                                            )
                                                        )}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={row.rowWidthFt ?? ''}
                                                        onChange={e => setRows(
                                                            rows.map((r, i) =>
                                                                i === idx ? { ...r, rowWidthFt: e.target.value } : r
                                                            )
                                                        )}
                                                        size="small"
                                                        variant="outlined"
                                                        type="number"
                                                        inputProps={{ min: 0, step: 0.01 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                        {row.message ?? ''}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedAttributes(row.attributeValues || {});
                                                            setAttributesModalOpen(true);
                                                        }}
                                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                        <div style={{
                            flex: 5,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    minHeight: 900,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #222',
                                    background: '#eee',
                                    marginTop: 0,
                                    flex: 1,
                                    display: 'block'
                                }}
                            >
                                {isLoaded ? (
                                    <div style={{ width: '100%', height: '900px' }}>
                                        <GoogleMap
                                            mapContainerStyle={{ width: '100%', height: '100%' }}
                                            center={mapCenter}
                                            zoom={17}
                                            onLoad={map => { mapRef.current = map; }}
                                            options={{
                                                mapTypeId: 'satellite',
                                                streetViewControl: false,
                                                fullscreenControl: false,
                                                mapTypeControl: true,
                                                mapTypeControlOptions: {
                                                    style: window.google && window.google.maps
                                                        ? window.google.maps.MapTypeControlStyle.DROPDOWN_MENU
                                                        : undefined,
                                                    position: window.google && window.google.maps
                                                        ? window.google.maps.ControlPosition.TOP_LEFT
                                                        : undefined
                                                }
                                            }}
                                        >
                                            {mapRows.map((row, idx) => (
                                                row.boundaries
                                                    ? getCoordinatesFromPolygonText(row.boundaries).map((polygon, polyIdx) => (
                                                        <Polygon
                                                            key={`${idx}-${polyIdx}`}
                                                            paths={polygon}
                                                            options={{
                                                                fillColor: '#43a047',
                                                                fillOpacity: 0.4,
                                                                strokeColor: '#333',
                                                                strokeOpacity: 0.95,
                                                                strokeWeight: 5,
                                                                clickable: false,
                                                                zIndex: 1
                                                            }}
                                                        />
                                                    ))
                                                    : null
                                            ))}
                                        </GoogleMap>
                                    </div>
                                ) : (
                                    <Typography sx={{ mt: 2, textAlign: 'center', color: '#888' }}>
                                        Loading map...
                                    </Typography>
                                )}
                            </Box>
                        </div>
                    </div>
                )}
                {rows.length > 0 && (
                    <>
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={saving || rows.filter(r => r.isSelected).length === 0}
                                sx={{ fontWeight: 'bold', px: 4, py: 1, borderRadius: 2 }}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                            {saveResult && saveResult.error && (
                                <Typography sx={{ color: 'red', mt: 2 }}>
                                    Error: {saveResult.error}
                                </Typography>
                            )}
                        </Box>
                        {/* Modal for SaveGisResponse.Results */}
                        <Modal
                            open={modalOpen}
                            onClose={() => setModalOpen(false)}
                            aria-labelledby="save-results-modal-title"
                            aria-describedby="save-results-modal-description"
                        >
                            <Paper
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    minWidth: 400,
                                    maxWidth: 600,
                                    p: 4,
                                    outline: 'none'
                                }}
                            >
                                <Typography id="save-results-modal-title" variant="h6" sx={{ mb: 2 }}>
                                    Save Results
                                </Typography>
                                {saveResult && saveResult.success && Array.isArray(saveResult.success.results) && (
                                    <Box
                                        id="save-results-modal-description"
                                        sx={{
                                            maxHeight: 300, // Adjust as needed
                                            overflowY: 'auto',
                                            mb: 2,
                                            border: '1px solid #eee',
                                            borderRadius: 1,
                                            background: '#fafafa',
                                            p: 2
                                        }}
                                    >
                                        {saveResult.success.results.length > 0 ? (
                                            
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {saveResult.success.results.map((result, idx) => (
                                                    <li key={idx}>
                                                        <Typography variant="body2">{result}</Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <Typography variant="body2">No results returned.</Typography>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ mt: 3, textAlign: 'right' }}>
                                    <Button variant="contained" onClick={() => {
                                        setModalOpen(false);
                                        navigate('/settings/blocks-view');
                                       }}>
                                        Close
                                    </Button>
                                </Box>
                            </Paper>
                        </Modal>
                    </>
                )}
            </Box>
            {/* Error dialog for name conflicts */}
            <Modal
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
                aria-labelledby="import-error-modal-title"
                aria-describedby="import-error-modal-description"
            >
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        minWidth: 400,
                        maxWidth: 600,
                        p: 4,
                        outline: 'none'
                    }}
                >
                    <Typography id="import-error-modal-title" variant="h6" sx={{ mb: 2, color: 'red' }}>
                        Block Name Errors
                    </Typography>
                    <Typography id="import-error-modal-description" variant="body1" sx={{ mb: 2 }}>
                        Please correct any block name errors highlighted in red and try again.
                    </Typography>
                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button variant="contained" onClick={() => setErrorDialogOpen(false)}>
                            Close
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* Modal for viewing block attributes */}
            <Modal
                open={attributesModalOpen}
                onClose={() => setAttributesModalOpen(false)}
                aria-labelledby="attributes-modal-title"
                aria-describedby="attributes-modal-description"
            >
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        minWidth: 350,
                        maxWidth: 500,
                        maxHeight: 500,
                        p: 3,
                        outline: 'none',
                    }}
                >
                    <Typography id="attributes-modal-title" variant="h6" sx={{ mb: 2 }}>
                        Block Attributes
                    </Typography>
                    <Box
                        id="attributes-modal-description"
                        sx={{
                            maxHeight: 300,
                            overflowY: 'auto',
                            border: '1px solid #eee',
                            borderRadius: 1,
                            background: '#fafafa',
                            p: 2,
                            mb: 2,
                        }}
                    >
                        {Object.keys(selectedAttributes).length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {Object.entries(selectedAttributes).map(([key, value]) => (
                                    <li key={key}>
                                        <Typography variant="body2">
                                            <strong>{key}:</strong> {value}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <Typography variant="body2">No attributes found.</Typography>
                        )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Button variant="contained" onClick={() => setAttributesModalOpen(false)}>
                            Close
                        </Button>
                    </Box>
                </Paper>
            </Modal>
        </div>
    );
}

export default BlocksImport;